<script setup lang="ts">
import {computed, onBeforeMount, provide, ref, watch, inject} from "vue";
import {useStore} from "vuex";
import SayErrorResponse from '@/mixins/sayErrorResponse';
import Say from '@/utils/Say';
import {useI18n} from "vue-i18n";
import { fetchCheckoutPeriodsCostOverview } from '@/api/checkoutPeriods';
import { fetchCanJobBeMarkedAsCompleted, markJobAsCompleted as markJobAsCompletedRequest } from '@/api/jobs';

import CreateProforma from "@/components/Jobs/View/Invoices/CreateProforma";
import ApproveProforma from "@/components/Jobs/View/Invoices/ApproveProforma";
import RejectProforma from "@/components/Jobs/View/Invoices/RejectProforma";
import ConvertToSalesInvoice from "@/components/Jobs/View/Invoices/ConvertToSalesInvoice";
import CreateNewVersionOfProForma from "@/components/Jobs/View/Invoices/CreateNewVersionOfProForma";
import MarkAsUncollectible from "@/components/Jobs/View/Invoices/MarkAsUncollectible";
import CreditSalesInvoice from "@/components/Jobs/View/Invoices/CreditSalesInvoice";
import OverviewRowActions from "@/components/Jobs/View/Invoices/OverviewRowActions";
import CheckoutPeriodRow from "./Invoices/CheckoutPeriodRow.vue";
import EditProForma from "./Invoices/EditProForma.vue";
import DeleteCheckoutPeriod from "./Invoices/DeleteCheckoutPeriod.vue";

const {t} = useI18n()
const store = useStore()
const jobId = inject('jobId', 0)
const job = inject('job', {})
const costOverview = ref([])
const editProFormaId = ref(0)
const canBeMarkedAsCompleted = ref(false)

provide('jobId', jobId)

onBeforeMount(async () => {
  refreshOverview()
});

const refreshOverview = () => {
  loadCheckoutPeriodsCostOverview()
  loadCanBeMarkedAsCompleted()
}

const loadCanBeMarkedAsCompleted = async () => {
  try {
    const response = await fetchCanJobBeMarkedAsCompleted(jobId.value);
    canBeMarkedAsCompleted.value = response.data.canBeMarkedAsCompleted;
  } catch (error) {
    SayErrorResponse(error);
  }
}

const markJobAsCompleted = async () => {
  try {
    await markJobAsCompletedRequest(jobId.value);
    await store.dispatch('jobs.fetchSingleForEdit', jobId.value);
    canBeMarkedAsCompleted.value = false;
    Say('success', t('jobMarkedAsCompleted'));
  } catch (error) {
    SayErrorResponse(error);
  }
}

const loadCheckoutPeriodsCostOverview = async () => {
  try {
    const response = await fetchCheckoutPeriodsCostOverview(jobId.value, true);
    if(!response.data.generated_at) {
      SayErrorResponse(response.data.error);
    } else {
      costOverview.value = response.data;
    }
  } catch (error) {
    SayErrorResponse(error);
  }
}

const deletedProForma = () => {
  editProFormaId.value = 0
  refreshOverview()
}

const finalizedProForma = () => {
  editProFormaId.value = 0
  refreshOverview()
}

const updatedProForma = () => {
  refreshOverview()
}

const formData = ref()

const dialogOpen = ref(false)
const dialogForm = ref(null)
const dialogFormModel = ref(null)
const checkoutPeriodSelected = ref(null)
const dialogTitle = ref(null)
const dialogLoading = ref(false)
provide('dialogTitle', dialogTitle)


watch(() => dialogForm.value, (newValue) => {
  dialogOpen.value = !!newValue
})

const formComponent = computed(() => {
  if (dialogForm.value == 'CreateProforma') {
    return CreateProforma;
  }
  if (dialogForm.value == 'ApproveProforma') {
    return ApproveProforma;
  }
  if (dialogForm.value == 'RejectProforma') {
    return RejectProforma;
  }
  if (dialogForm.value == 'CreateNewVersionOfProForma') {
    return CreateNewVersionOfProForma;
  }
  if (dialogForm.value == 'ConvertToSalesInvoice') {
    return ConvertToSalesInvoice;
  }
  if (dialogForm.value == 'MarkAsUncollectible') {
    return MarkAsUncollectible;
  }
  if (dialogForm.value == 'CreditSalesInvoice') {
    return CreditSalesInvoice;
  }
  return null
})

const dialogFormSubmitDisabled = computed(() => {
  // Check if the formComponent is equal to ConvertToSalesInvoice or CreditSalesInvoice,
  // and if this client has an exact online account linked including an email address
  if (dialogForm.value == 'ConvertToSalesInvoice' || dialogForm.value == 'CreditSalesInvoice') {
    return !job.value.client.exact_online_account_id || !job.value.client.exact_online_account.email
  }
  // Check if the formComponent is equal to CreateProforma,
  // and if this client has a complete address
  if (dialogForm.value == 'CreateProforma') {
    return !job.value.client.name || !job.value.client.postcode || !job.value.client.city || !job.value.client.address
  }
})

const dialogIcon = computed(() => {
  if (dialogForm.value == 'CreateProforma') {
    return 'mdi-file-plus-outline';
  }
  if (dialogForm.value == 'ApproveProforma') {
    return 'mdi-file-check-outline';
  }
  if (dialogForm.value == 'RejectProforma') {
    return 'mdi-file-remove-outline';
  }
  if (dialogForm.value == 'CreateNewVersionOfProForma') {
    return 'mdi-file-edit-outline';
  }
  if (dialogForm.value == 'ConvertToSalesInvoice') {
    return 'mdi-file-check-outline';
  }
  if (dialogForm.value == 'MarkAsUncollectible') {
    return 'mdi-file-remove-outline';
  }
  return 'mdi-pencil';
})

const dialogBtnText = computed(() => {
  if (dialogForm.value == 'CreateProforma') {
    return t('create');
  }
  if (dialogForm.value == 'ApproveProforma') {
    return t('approve');
  }
  if (dialogForm.value == 'RejectProforma') {
    return t('reject');
  }
  if (dialogForm.value == 'CreateNewVersionOfProForma') {
    return t('createNewVersionOfProForma');
  }
  if (dialogForm.value == 'ConvertToSalesInvoice') {
    return t('createSalesInvoice');
  }
  if (dialogForm.value == 'MarkAsUncollectible') {
    return t('markAsUncollectible');
  }
  if (dialogForm.value == 'CreditSalesInvoice') {
    return t('creditSalesInvoice');
  }
  return t('save');
})

const triggerRowAction = ({action, invoice, checkoutPeriod}) => {
  checkoutPeriodSelected.value = null

  if (action === 'openApproveDialog') {
    dialogFormModel.value = invoice
    dialogForm.value = 'ApproveProforma'
  } else if (action === 'openRejectDialog') {
    dialogFormModel.value = invoice
    dialogForm.value = 'RejectProforma'
  } else if (action === 'openConvertToSalesInvoiceDialog') {
    dialogFormModel.value = invoice
    dialogForm.value = 'ConvertToSalesInvoice'
  } else if (action === 'openCreateNewVersionOfProFormaDialog') {
    dialogFormModel.value = invoice
    dialogForm.value = 'CreateNewVersionOfProForma'
  } else if (action === 'openMarkAsUncollectibleDialog') {
    dialogFormModel.value = invoice
    dialogForm.value = 'MarkAsUncollectible'
  } else if (action === 'openCreateDialog') {
    dialogFormModel.value = {
      dateFrom: checkoutPeriod.start_date,
      dateTo: checkoutPeriod.end_date
    }
    checkoutPeriodSelected.value = checkoutPeriod
    dialogForm.value = 'CreateProforma'
  } else if (action === 'openCreditSalesInvoiceView') {
    dialogFormModel.value = invoice
    dialogForm.value = 'CreditSalesInvoice'
  } else if (action === 'openEditView') {
    editProFormaId.value = invoice.id
  }else {
    throw Error(`Unknown Action: ${action}`)
  }
}

const dialogClosed = () => {
  dialogForm.value = null
  dialogFormModel.value = null
  dialogLoading.value = false
  refreshOverview()
}

const formatDate = (date: string) => {
  if (!date) {return ''}
  const [year, month, day] = date.split('-');
  return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
}
provide('formatDate', formatDate)
</script>

<template>

  <v-row v-if="job.use_old_invoicing === true">
    <v-col>
      <p class="px-2 py-3"><i>{{ t('jobUsesOldInvoicingMethod') }}</i></p>
    </v-col>
  </v-row>

  <v-row v-else-if="editProFormaId === 0" class="mb-4">
    <v-col class="overflow-auto">
      <template v-if="job.id && costOverview.generated_at && (Object.keys(costOverview.equipment).length || Object.keys(costOverview.lettering).length || Object.keys(costOverview.hours).length || Object.keys(costOverview.additionalCosts).length)">
      <template v-if="!job.client_responsible_1 || !job.client_responsible_1.email">
          <v-alert type="warning" class="mb-5" density="compact">
              {{ t('clientResponsibleNotSetOrEmailMissing') }}
              <router-link
                  :to="{ name: 'JobsEdit', params: { id: job.job_id } }"
                  class="text-white"
              >
                  {{ t('editJob') }}
              </router-link>
          </v-alert>
      </template>
      <template v-if="job.client.name === null || job.client.postcode === null || job.client.city === null || job.client.address === null">
        <v-alert type="warning" class="mb-5" density="compact">
          {{ t('clientHasIncompleteAddress') }}
          <router-link
            :to="{ name: 'ClientsEdit', params: { id: job.client.id } }"
            class="text-white"
          >
            {{ t('clickHereToCompleteTheAddress') }}
          </router-link>
        </v-alert>
      </template>
      <template v-if="job.client.exact_online_account_id == null">
        <v-alert type="warning" class="mb-5" density="compact">
          {{ t('clientIsNotLinkedToAnExactOnlineAccount') }}
          <router-link
            :to="{ name: 'ClientsEdit', params: { id: job.client.id } }"
            class="text-white"
          >
            {{ t('clickHereToLinkClientToAnExactOnlineAccount') }}
          </router-link>
        </v-alert>
      </template>
      <template v-else-if="job.client.exact_online_account.email == null || job.client.exact_online_account.email == ''">
        <v-alert type="warning" class="mb-5" density="compact">
          {{ t('exactOnlineAccountHasNoEmailAddress', { name: job.client.exact_online_account.code }) }}
          {{ t('pleaseCompleteItInExactOnline') }}
          {{ t('whenCompleted') }},
          <router-link
            :to="{ name: 'ClientsList' }"
            class="text-white"
          >
            {{ t('goToThisPageToSynchronizeChanges') }}
          </router-link>
        </v-alert>
      </template>
      <table class="checkout-periods-table">
        <thead>
          <tr>
            <th class="custom_header_table custom-bg">{{ t('checkoutPeriod') }}</th>
            <!-- <th class="custom_header_table custom-bg">{{ t('type') }}</th> -->
            <th class="custom_header_table custom-bg">{{ t('number') }}</th>
            <th class="custom_header_table custom-bg"></th>
            <th class="custom_header_table custom-bg">{{ t('emailStatus') }}</th>
            <th class="custom_header_table custom-bg">{{ t('date') }}</th>
            <th class="custom_header_table custom-bg text-right">{{ t('invoiceTotalExcl') }}</th>
            <th class="custom_header_table custom-bg">{{ t('status') }}</th>
            <th class="custom_header_table custom-bg">{{ t('actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="checkoutPeriod in costOverview.checkout_periods" :key="checkoutPeriod.id">
            <template v-if="checkoutPeriod.id > 0">
              <!-- First row with checkout period date and first pro forma -->
              <tr :class="{'bg-deleted': checkoutPeriod.deleted_at }">
                <td :rowspan="checkoutPeriod.invoices.length > 0 ? checkoutPeriod.invoices.length : 1" :class="{'bg-deleted': checkoutPeriod.deleted_at, 'bg-white': !checkoutPeriod.deleted_at, 'border-left' : true}">
                  {{ formatDate(checkoutPeriod.start_date) }} {{ t('upToAndIncludingAbbreviation') }} {{ formatDate(checkoutPeriod.end_date) }}
                  <template v-if="checkoutPeriod.has_only_rejected_pro_pormas">
                    &nbsp; <span class="lowercase" v-if="checkoutPeriod.deleted_at">({{ t('deleted') }})</span>
                    <template v-else>
                      <DeleteCheckoutPeriod :checkoutPeriod="checkoutPeriod" @deleted="refreshOverview" />
                    </template>
                  </template>
                </td>
                <template v-if="checkoutPeriod.invoices.length">
                  <CheckoutPeriodRow :invoice="checkoutPeriod.invoices[0]" @refreshInvoices="refreshOverview" />
                  <td width="140px">
                    <OverviewRowActions v-if="!checkoutPeriod.deleted_at" :invoice="checkoutPeriod.invoices[0]" :checkout-period="checkoutPeriod" @trigger="triggerRowAction" :lastInvoiceIsRejectedProForma="checkoutPeriod.invoices.length === 1 && checkoutPeriod.invoices[0].status === 'REJECTED'" :lastInvoiceIsCreditNote="checkoutPeriod.invoices.length === 1 && checkoutPeriod.invoices[0].type === 'credit_note'" />
                  </td>
                </template>
                <template v-else>
                  <td colspan="6"></td>
                  <td>
                    <OverviewRowActions v-if="!checkoutPeriod.deleted_at" :invoice="{}" :checkout-period="checkoutPeriod" @trigger="triggerRowAction" />
                  </td>
                </template>
              </tr>
              <!-- Additional rows for the remaining invoices -->
              <tr v-for="invoice in checkoutPeriod.invoices.slice(1)" :key="invoice.id" :class="{'bg-deleted': checkoutPeriod.deleted_at }">
                  <CheckoutPeriodRow :invoice="invoice" @refreshInvoices="refreshOverview" />
                  <td width="140px">
                    <OverviewRowActions v-if="!checkoutPeriod.deleted_at" :invoice="invoice" :checkout-period="checkoutPeriod" @trigger="triggerRowAction" :lastInvoiceIsRejectedProForma="checkoutPeriod.invoices.indexOf(invoice) === checkoutPeriod.invoices.length - 1 && invoice.status === 'REJECTED'" :lastInvoiceIsCreditNote="checkoutPeriod.invoices.indexOf(invoice) === checkoutPeriod.invoices.length - 1 && invoice.type === 'credit_note'" />
                  </td>
              </tr>
            </template>
            <template v-else>
              <tr>
                <td class="border-left">{{ formatDate(checkoutPeriod.start_date) }} {{ t('upToAndIncludingAbbreviation') }} {{ formatDate(checkoutPeriod.end_date) }} &nbsp; ({{ t('concept') }})</td>
                <td colspan="7">
                  <template v-if="!checkoutPeriod.is_future">
                    <i>{{ t('conceptCheckoutPeriodInsctructions') }}</i>
                  </template>
                  <template v-else>
                    <i>{{ t('futureConceptCheckoutPeriodInsctructions') }}</i>
                  </template>
                </td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
      </template>
      <div v-else-if="costOverview.generated_at">
        <p><i>{{ t('noDataAvailable') }}</i></p>
      </div>
      <div v-else>
        <p>
          <v-progress-circular indeterminate color="primary" :size="16" width="3" class="mr-2"></v-progress-circular>
          <i>{{ t('overviewIsBeingLoaded') }}</i>
        </p>
      </div>

    </v-col>
    <v-col v-if="canBeMarkedAsCompleted" cols="12" class="mt-1">
      <p class="mb-2">{{ t('isThisProjectCompleted') }}</p>
      <v-btn color="primary" @click="markJobAsCompleted">{{ t('markJobAsCompleted') }}</v-btn>
    </v-col>
  </v-row>

  <template v-else>
    <EditProForma :proFormaId="editProFormaId" @cancelEditingProForma="editProFormaId = 0" @deletedProForma="deletedProForma()" @finalizedProForma="finalizedProForma()" @updatedProForma="updatedProForma()" />
  </template>

  <v-dialog
    v-model="dialogOpen"
    :width="dialogForm === 'ConvertToSalesInvoice' || dialogForm === 'CreateNewVersionOfProForma' ? 1100 : 600"
    id="invoicing-action-dialog"
    @update:model-value="dialogClosed()"
  >
    <v-card
      :max-width="dialogForm === 'ConvertToSalesInvoice' || dialogForm === 'CreateNewVersionOfProForma' ? 1100 : 600"
      :prepend-icon="dialogIcon"
      :title="dialogTitle"
    >
      <template v-slot:text>
        <formComponent
          v-if="formComponent"
          ref="formData"
          :job="job"
          :invoice="dialogFormModel"
          :checkout-period="checkoutPeriodSelected"
          @created="dialogClosed()"
          @approved="dialogClosed()"
          @rejected="dialogClosed()"
          @converted-to-sales-invoice="dialogClosed()"
          @marked-as-uncollectible="dialogClosed()"
          @sales-invoice-credited="dialogClosed()"
          @cancel-form="dialogClosed()"
          @loading="dialogLoading = $event"
        />
      </template>
      <template v-slot:actions>
        <v-btn
          :text="dialogBtnText"
          @click="$refs.formData.submit()"
          type="submit"
          :loading="dialogLoading"
          :disabled="dialogFormSubmitDisabled"
        />
        <v-btn
          :text="t('cancel')"
          @click="$refs.formData.cancel()"
          :disabled="dialogLoading"
        />
      </template>
    </v-card>
  </v-dialog>

</template>
<style lang="scss" scoped>
.v-dialog#invoicing-action-dialog  > .v-overlay__content > .v-card,
.v-dialog#invoicing-action-dialog  > .v-overlay__content > .v-sheet,
.v-dialog#invoicing-action-dialog  > .v-overlay__content > form > .v-card,
.v-dialog#invoicing-action-dialog  > .v-overlay__content > form > .v-sheet {
  overflow: initial;
}

.v-dialog#invoicing-action-dialog  > .v-overlay__content > .v-card .v-card-item {
  background: var(--custom-bg) !important;
  margin-bottom: 15px;
}

.no-wrap {
  white-space: nowrap;
}

.checkout-periods-table {
  width: 100%;
  border-spacing: 0;
  background-color: #fff;
  font-size: 10.5px;
}

:deep(.checkout-periods-table tr) {
  transition-duration: 0.28s;
  transition-property: 'box-shadow', 'opacity', 'background', 'height';
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

  td {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    &.border-left {
      border-left: 1px solid rgba(0, 0, 0, 0.12);
    }
    &:last-child {
      border-right: 1px solid rgba(0, 0, 0, 0.12);
    }
  }

  th {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    &:first-child {
      border-top-left-radius: 4px;
    }
    &:last-child {
      border-top-right-radius: 4px;
    }
  }
  &:last-child {
    td {
      &:first-child {
        border-bottom-left-radius: 4px;
      }
      &:last-child {
        border-bottom-right-radius: 4px;
      }
    }
  }



}

.checkout-periods-table tr:hover {
  background-color: rgba(0, 0, 0, 0.07);
}

.checkout-periods-table th, .checkout-periods-table td {
  vertical-align: top;
  white-space: nowrap;
}

.checkout-periods-table th {
  padding: 12px 16px;
  text-align: left;
  background-color: #fff;
  font-weight: 500;
}

.checkout-periods-table td {
  padding: 10px 16px;
}

.bg-white {
  background-color: #fff;
}
.bg-deleted {
  background-color: #fce0de !important;
}

.lowercase {
  text-transform: lowercase;
}
</style>
